import React, { useState, useRef, useEffect } from 'react';
import ProseMirrorEditor from '../../common/ProseMirrorEditor';
import { updateTaskComment, deleteTaskComment, getSiteUrl } from '../../../utils/api';

const TaskCommentsSection = ({
    taskComments,
    formatRelativeTime,
    taskData,
    currentUserId,
    onAddComment,
    projectId,
    taskId,
    onRefreshComments,
}) => {
    const [previewAttachment, setPreviewAttachment] = useState(null);
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editingCommentContent, setEditingCommentContent] = useState('');
    const [editingCommentFiles, setEditingCommentFiles] = useState([]);
    const [deletedFileIds, setDeletedFileIds] = useState([]);
    const [showDropdownId, setShowDropdownId] = useState(null);
    const dropdownRefs = useRef({});

    const handleAttachmentPreview = (file, previewUrl) => {
        if (!file?.file_url) return;
        setPreviewAttachment({
            url: previewUrl,
            name: file.file_name || 'Attachment',
        });
    };

    const handleClosePreview = () => {
        setPreviewAttachment(null);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            Object.values(dropdownRefs.current).forEach((ref) => {
                if (ref && !ref.contains(event.target) && !event.target.closest('.task-detail-comment-dropdown') && !event.target.closest('.task-comment-attachment-overlay')) {
                    setShowDropdownId(null);
                }
            });
        };

        if (showDropdownId !== null) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdownId]);

    const handleEditComment = (comment) => {
        setEditingCommentId(comment.id);
        // Decode HTML entities for editing
        const decodedContent = comment.content
            .replace(/&#x([0-9a-fA-F]+);/g, (match, hex) => {
                const codePoint = parseInt(hex, 16);
                try {
                    return String.fromCodePoint(codePoint);
                } catch {
                    return match;
                }
            })
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'");
        setEditingCommentContent(decodedContent);
        // Set existing files for editing
        setEditingCommentFiles(comment.files || []);
        setDeletedFileIds([]);
        setShowDropdownId(null);
    };

    const handleSaveEdit = async (commentId, editorData) => {
        if (!projectId || !taskId) {
            setEditingCommentId(null);
            return;
        }

        // Use editor data if provided (from ProseMirrorEditor), otherwise use textarea content
        const content = editorData?.content || editorData?.htmlContent || editingCommentContent.trim();
        const htmlContent = editorData?.htmlContent || content;
        const newAttachments = editorData?.attachments || [];
        const mentions = editorData?.mentions || [];

        if (!content && newAttachments.length === 0 && editingCommentFiles.length === deletedFileIds.length) {
            setEditingCommentId(null);
            return;
        }

        try {
            const formData = new FormData();
            formData.append('content', content);
            formData.append('html_content', htmlContent);

            if (mentions.length > 0) {
                formData.append('mentions', JSON.stringify(mentions));
            }

            // Add files to delete
            if (deletedFileIds.length > 0) {
                formData.append('files_to_delete', JSON.stringify(deletedFileIds));
            }

            // Add new attachments
            if (newAttachments.length > 0) {
                newAttachments.forEach((att, index) => {
                    const file = att?.file || att;
                    if (!file) return;
                    const name = file.name || `attachment-${index}`;
                    formData.append('attachments[]', file, name);
                });
            }

            await updateTaskComment(projectId, taskId, commentId, formData);
            
            if (typeof onRefreshComments === 'function') {
                await onRefreshComments();
            }
            
            setEditingCommentId(null);
            setEditingCommentContent('');
            setEditingCommentFiles([]);
            setDeletedFileIds([]);
        } catch (err) {
            console.error('Failed to update comment:', err);
            alert('Failed to update comment. Please try again.');
        }
    };

    const handleCancelEdit = () => {
        setEditingCommentId(null);
        setEditingCommentContent('');
        setEditingCommentFiles([]);
        setDeletedFileIds([]);
    };

    const handleDeleteAttachmentInEdit = (fileId) => {
        setDeletedFileIds([...deletedFileIds, fileId]);
        setEditingCommentFiles(editingCommentFiles.filter(file => file.id !== fileId));
    };

    const handleDeleteComment = async (commentId) => {
        if (!projectId || !taskId) return;

        if (!window.confirm('Are you sure you want to delete this comment?')) {
            return;
        }

        try {
            await deleteTaskComment(projectId, taskId, commentId);
            
            if (typeof onRefreshComments === 'function') {
                await onRefreshComments();
            }
            
            setShowDropdownId(null);
        } catch (err) {
            console.error('Failed to delete comment:', err);
            alert('Failed to delete comment. Please try again.');
        }
    };

    const toggleDropdown = (commentId, e) => {
        e.stopPropagation();
        setShowDropdownId(showDropdownId === commentId ? null : commentId);
    };

    return (
        <>
            <div className="task-detail-comments-list">
                {taskComments && taskComments.length > 0 ? (
                    taskComments.map((comment) => (
                        <div key={comment.id} className="task-detail-comment-item">
                            <div className="task-detail-comment-avatar">
                                {comment.creator?.image || comment.creator?.avatar_url ? (
                                    <img
                                        src={
                                            comment.creator.image ||
                                            comment.creator.avatar_url
                                        }
                                        alt={
                                            comment.creator.display_name ||
                                            comment.creator.name
                                        }
                                    />
                                ) : (
                                    <div className="task-detail-avatar-placeholder">
                                        {(
                                            comment.creator?.display_name ||
                                            comment.creator?.name ||
                                            'U'
                                        )
                                            .charAt(0)
                                            .toUpperCase()}
                                    </div>
                                )}
                            </div>
                            <div className="task-detail-comment-content">
                                <div className="task-detail-comment-header">
                                    <div className="task-detail-comment-text">
                                        <strong>
                                            {comment.creator?.display_name ||
                                                comment.creator?.name ||
                                                'User'}
                                        </strong>
                                        {editingCommentId === comment.id ? (
                                            <div className="task-detail-comment-edit-wrapper">
                                                {/* Show existing attachments that haven't been deleted */}
                                                {editingCommentFiles.length > 0 && (
                                                    <div className="task-detail-comment-edit-attachments">
                                                        {editingCommentFiles.map((file) => {
                                                            let previewUrl = file.file_url;

                                                            if(file._attached_file) {
                                                                const attachedFilePath = file._attached_file.replace("/wp-content/uploads", '');
                                                                previewUrl = `${getSiteUrl()}/wp-content/uploads/${attachedFilePath}`;
                                                            }

                                                            const displayName = file.file_name || 'Attachment';
                                                            const key = file.id || file.attachment_id;
                                                            const mime = file.mime_type || '';
                                                            const type = (file.type || '').toLowerCase();
                                                            const isPhoto = type === 'photo' || mime.startsWith('image/');
                                                            const isVideo = type === 'video' || mime.startsWith('video/');

                                                            return (
                                                                <div key={key} className="task-detail-comment-edit-attachment-item">
                                                                    {isPhoto && previewUrl ? (
                                                                        <div className="task-detail-comment-edit-attachment-image">
                                                                            <img src={previewUrl} alt={displayName} />
                                                                            <button
                                                                                type="button"
                                                                                className="task-detail-comment-edit-attachment-delete"
                                                                                onClick={() => handleDeleteAttachmentInEdit(file.id || file.attachment_id)}
                                                                                title="Delete attachment"
                                                                            >
                                                                                <i className="fa-solid fa-times"></i>
                                                                            </button>
                                                                        </div>
                                                                    ) : isVideo && previewUrl ? (
                                                                        <div className="task-detail-comment-edit-attachment-video">
                                                                            <video src={previewUrl} controls={false} />
                                                                            <button
                                                                                type="button"
                                                                                className="task-detail-comment-edit-attachment-delete"
                                                                                onClick={() => handleDeleteAttachmentInEdit(file.id || file.attachment_id)}
                                                                                title="Delete attachment"
                                                                            >
                                                                                <i className="fa-solid fa-times"></i>
                                                                            </button>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="task-detail-comment-edit-attachment-file">
                                                                            <i className="fa-solid fa-file"></i>
                                                                            <span>{displayName}</span>
                                                                            <button
                                                                                type="button"
                                                                                className="task-detail-comment-edit-attachment-delete"
                                                                                onClick={() => handleDeleteAttachmentInEdit(file.id || file.attachment_id)}
                                                                                title="Delete attachment"
                                                                            >
                                                                                <i className="fa-solid fa-times"></i>
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                <div className="task-detail-comment-edit-editor-wrapper">
                                                    <ProseMirrorEditor
                                                        key={`edit-comment-${comment.id}`}
                                                        currentUser={
                                                            taskData?.project_users?.find(
                                                                (u) => (u.ID || u.id) == currentUserId
                                                            ) || {}
                                                        }
                                                        projectUsers={taskData?.project_users || []}
                                                        onSubmit={(editorData) => handleSaveEdit(comment.id, editorData)}
                                                        onCancel={handleCancelEdit}
                                                        placeholder="Edit comment"
                                                        initialValue={editingCommentContent}
                                                    />
                                                    <div className="task-detail-comment-edit-cancel-wrapper">
                                                        <button
                                                            type="button"
                                                            className="task-detail-comment-edit-cancel-btn"
                                                            onClick={handleCancelEdit}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: comment.content,
                                                }}
                                            />
                                        )}
                                    </div>
                                    {(comment.created_by == currentUserId || comment.creator?.ID == currentUserId || comment.creator?.id == currentUserId) && (
                                        <div className="task-detail-comment-actions" ref={el => dropdownRefs.current[comment.id] = el}>
                                            <button
                                                className="task-detail-comment-menu-btn"
                                                onClick={(e) => toggleDropdown(comment.id, e)}
                                                title="More options"
                                            >
                                                <i className="fa-solid fa-ellipsis"></i>
                                            </button>
                                            {showDropdownId === comment.id && (
                                                <div className="task-detail-comment-dropdown">
                                                    <button
                                                        className="task-detail-comment-dropdown-item"
                                                        onClick={() => handleEditComment(comment)}
                                                    >
                                                        <i className="fa-solid fa-edit"></i>
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="task-detail-comment-dropdown-item task-detail-comment-dropdown-item--delete"
                                                        onClick={() => handleDeleteComment(comment.id)}
                                                    >
                                                        <i className="fa-solid fa-trash"></i>
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {comment.files && comment.files.length > 0 && (
                                    <div className="task-detail-comment-attachments-block">
                                        <div className="task-detail-comment-attachments-header">
                                            Attachments ({comment.files.length})
                                        </div>
                                        <div className="task-comment-attachments">
                                        {comment.files.map((file, index) => {
                                            
                                            let previewUrl = file.file_url;
                                            const displayName = file.file_name || 'Attachment';
                                            let downloadUrl = file.file_url;

                                            if(file._attached_file) {
                                                const attachedFilePath = file._attached_file.replace("/wp-content/uploads", '');
                                                previewUrl = `${getSiteUrl()}/wp-content/uploads/${attachedFilePath}`;
                                                downloadUrl = `${getSiteUrl()}/wp-content/uploads/${attachedFilePath}`;
                                            }
                                            const key = file.id || file.attachment_id || index;
                                            const mime = file.mime_type || '';
                                            const type = (file.type || '').toLowerCase();
                                            const isPhoto =
                                                type === 'photo' ||
                                                mime.startsWith('image/');
                                            const isVideo =
                                                type === 'video' ||
                                                mime.startsWith('video/');

                                            if (isPhoto) {
                                                return (
                                                    <div
                                                        key={key}
                                                        className="task-comment-attachment-card task-comment-attachment-card--image"
                                                    >
                                                        {previewUrl ? (
                                                            <div className="task-comment-attachment-thumb task-comment-attachment-thumb--image">
                                                                <img
                                                                    src={previewUrl}
                                                                    alt={displayName}
                                                                    className="task-comment-attachment-thumb-img"
                                                                />
                                                                <div className="task-comment-attachment-overlay">
                                                                    <button
                                                                        className="task-comment-attachment-preview-btn"
                                                                        onClick={() => handleAttachmentPreview(file, previewUrl)}
                                                                        title="Preview"
                                                                    >
                                                                        <i className="fa-solid fa-eye"></i>
                                                                    </button>
                                                                    {downloadUrl && (
                                                                        <a
                                                                            href={downloadUrl}
                                                                            download
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="task-comment-attachment-download-btn"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            title="Download"
                                                                        >
                                                                            <i className="fa-solid fa-download"></i>
                                                                        </a>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="task-comment-attachment-fallback">
                                                                {displayName}
                                                            </span>
                                                        )}
                                                        {/* <div className="task-comment-attachment-filename">
                                                            {displayName}
                                                        </div> */}
                                                    </div>
                                                );
                                            }

                                            if (isVideo) {
                                                return (
                                                    <div
                                                        key={key}
                                                        className="task-comment-attachment-card task-comment-attachment-card--video"
                                                    >
                                                        {previewUrl ? (
                                                            <div className="task-comment-attachment-video-wrapper">
                                                                <video
                                                                    controls={false}
                                                                    src={previewUrl}
                                                                    className="task-comment-attachment-video"
                                                                >
                                                                    {displayName}
                                                                </video>
                                                                <div className="task-comment-attachment-overlay">
                                                                    {downloadUrl && (
                                                                        <a
                                                                            href={downloadUrl}
                                                                            download
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="task-comment-attachment-download-btn"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            title="Download"
                                                                        >
                                                                            <i className="fa-solid fa-download"></i>
                                                                        </a>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="task-comment-attachment-fallback">
                                                                {displayName}
                                                            </span>
                                                        )}
                                                        {/* <div className="task-comment-attachment-filename">
                                                            {displayName}
                                                        </div> */}
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div
                                                    key={key}
                                                    className="task-comment-attachment-card task-comment-attachment-card--file"
                                                >
                                                    <div className="task-comment-attachment-file-icon">
                                                        📄
                                                    </div>
                                                    <div className="task-comment-attachment-meta">
                                                        <div className="task-comment-attachment-filename">
                                                            {displayName}
                                                        </div>
                                                    </div>
                                                    <div className="task-comment-attachment-file-overlay">
                                                        {downloadUrl ? (
                                                            <a
                                                                href={downloadUrl}
                                                                download
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="task-comment-attachment-download-btn"
                                                                onClick={(e) => e.stopPropagation()}
                                                                title="Download"
                                                            >
                                                                <i className="fa-solid fa-download"></i>
                                                            </a>
                                                        ) : (
                                                            <span className="task-comment-attachment-unavailable">
                                                                Unavailable
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        </div>
                                    </div>
                                )}

                                <div className="task-detail-comment-time">
                                    {formatRelativeTime(comment.created_at) && (
                                        <span>
                                            · {formatRelativeTime(comment.created_at)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="task-detail-no-comments">No comments yet</div>
                )}
            </div>
            <ProseMirrorEditor
                currentUser={
                    taskData?.project_users?.find(
                        (u) => (u.ID || u.id) == currentUserId
                    ) || {}
                }
                projectUsers={taskData?.project_users || []}
                onSubmit={onAddComment}
                placeholder="Add a comment"
            />

            {previewAttachment && (
                <div className="octopus-preview-overlay" onClick={handleClosePreview}>
                    <div
                        className="octopus-preview-content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="octopus-preview-close"
                            onClick={handleClosePreview}
                        >
                            <span aria-hidden="true">×</span>
                            <span className="octopus-sr-only">Close preview</span>
                        </button>

                        <img
                            src={previewAttachment.url}
                            alt={previewAttachment.name}
                            className="octopus-preview-image"
                        />
                        {previewAttachment.name && (
                            <div className="octopus-preview-title">
                                {previewAttachment.name}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default TaskCommentsSection;


